import React, { useEffect, useState } from 'react';
import { smartFactureAPI, paymentsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  FileText, Plus, Download, Send, CheckCircle, Clock, AlertCircle, 
  DollarSign, Camera, Shield, TrendingUp, ArrowRight, Sparkles,
  AlertTriangle, Eye, TestTube, Search, X, Filter, Users, FileEdit,
  Share2, Inbox, Calculator, RefreshCw, Bell, BarChart3, FileSpreadsheet,
  CreditCard, Package, Upload, Headphones, UsersRound, Building2, Smartphone
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import PPFSimulationModal from '../../components/smartfacture/PPFSimulationModal';
import ShareInvoiceModal from '../../components/smartfacture/ShareInvoiceModal';
import TrialPaywall from '../../components/trial/TrialPaywall';
import useTrialGuard from '../../hooks/useTrialGuard';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import QuickStartWizard from '../../components/smartfacture/QuickStartWizard';
import TestimonialForm from '../../components/TestimonialForm';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartFactureDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [quickStartChecked, setQuickStartChecked] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  
  // Trial guard hook
  const { 
    showPaywall, 
    paywallReason, 
    paywallAction, 
    closePaywall, 
    handleTrialError 
  } = useTrialGuard();

  // Check if QuickStart wizard should be shown
  useEffect(() => {
    const checkQuickStart = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/quickstart/should-show`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setShowQuickStart(data.show);
      } catch (error) {
        console.error('Error checking quickstart:', error);
      } finally {
        setQuickStartChecked(true);
      }
    };
    checkQuickStart();
  }, []);

  useEffect(() => {
    loadDashboard();
  }, []);

  // Filter invoices when search query changes
  useEffect(() => {
    if (dashboard?.recent_invoices && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const filtered = dashboard.recent_invoices.filter(invoice => {
        // Search by invoice number
        if (invoice.invoice_number?.toLowerCase().includes(query)) return true;
        // Search by client name
        if (invoice.client_name?.toLowerCase().includes(query)) return true;
        // Search by amount (allow partial match like "150" or "150.00")
        if (invoice.grand_total?.toString().includes(query)) return true;
        // Search by status in French
        const statusMap = {
          'paid': ['payée', 'paye', 'payé'],
          'pending': ['attente', 'en attente'],
          'overdue': ['retard', 'en retard'],
          'draft': ['brouillon']
        };
        const statusTerms = statusMap[invoice.status] || [];
        if (statusTerms.some(term => term.includes(query))) return true;
        return false;
      });
      setFilteredInvoices(filtered);
    } else {
      setFilteredInvoices(dashboard?.recent_invoices || []);
    }
  }, [searchQuery, dashboard?.recent_invoices]);

  const loadDashboard = async () => {
    try {
      const response = await smartFactureAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (invoiceId) => {
    try {
      await smartFactureAPI.generatePDF(invoiceId);
    } catch (error) {
      console.error('PDF generation failed', error);
    }
  };

  const handleSimulate = (invoice) => {
    setSelectedInvoice(invoice);
    setShowSimulationModal(true);
  };

  const handleSimulationComplete = () => {
    loadDashboard(); // Refresh data
  };

  const handleMarkAsPaid = async (invoice) => {
    try {
      await paymentsAPI.markAsPaid(invoice.id);
      loadDashboard(); // Refresh data
    } catch (error) {
      console.error('Failed to mark as paid', error);
    }
  };

  const handleMarkAsUnpaid = async (invoice) => {
    try {
      await paymentsAPI.markAsUnpaid(invoice.id);
      loadDashboard(); // Refresh data
    } catch (error) {
      console.error('Failed to mark as unpaid', error);
    }
  };

  const handleShare = (invoice) => {
    setSelectedInvoice(invoice);
    setShowShareModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Chiffre d\'affaires',
      value: `${dashboard?.total_revenue?.toFixed(2) || '0.00'} €`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      testId: 'total-revenue'
    },
    {
      title: 'En attente',
      value: `${dashboard?.pending_amount?.toFixed(2) || '0.00'} €`,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      testId: 'pending-amount'
    },
    {
      title: 'En retard',
      value: `${dashboard?.overdue_amount?.toFixed(2) || '0.00'} €`,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      testId: 'overdue-amount'
    },
    {
      title: 'Factures',
      value: dashboard?.total_invoices || 0,
      icon: FileText,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      testId: 'total-invoices'
    },
  ];

  const complianceRate = dashboard?.compliance?.compliance_rate || 100;

  return (
    <div className="space-y-8" data-testid="smartfacture-dashboard">
      {/* Header - More elegant */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{fontFamily: "'Outfit', sans-serif"}}>
            SmartFacture AI
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Votre solution de facturation intelligente
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard/smartfacture/scan')}
            className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300 rounded-xl transition-all"
            data-testid="scan-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            Scanner
          </Button>
          <Link to="/dashboard/smartfacture/create">
            <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 rounded-xl shadow-md shadow-emerald-500/20 transition-all" data-testid="create-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle facture
            </Button>
          </Link>
        </div>
      </div>

      {/* Search Bar - More elegant */}
      <Card className="border-slate-200/80 shadow-sm overflow-visible bg-white/80 backdrop-blur-sm">
        <CardContent className="p-4">
          <div className="relative">
            <div className={`relative flex items-center transition-all duration-300 ${searchFocused ? 'ring-2 ring-emerald-400/50 ring-offset-2' : ''} rounded-xl`}>
              <div className="absolute left-4 pointer-events-none">
                <Search className={`h-5 w-5 transition-colors duration-200 ${searchFocused ? 'text-emerald-500' : 'text-slate-400'}`} />
              </div>
              <Input
                type="text"
                placeholder="Rechercher une facture, un client, un montant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="pl-12 pr-10 h-12 text-base border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:border-emerald-400 focus:ring-0 transition-all"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 p-1.5 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              )}
            </div>
            
            {/* Search hints */}
            {searchFocused && !searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-white rounded-xl shadow-xl border border-slate-100 z-50">
                <p className="text-xs font-medium text-slate-500 mb-3">RECHERCHER PAR</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <button 
                    onClick={() => setSearchQuery('FAC-')}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                  >
                    <FileText className="h-4 w-4 text-slate-400" />
                    N° facture
                  </button>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                  >
                    <DollarSign className="h-4 w-4 text-slate-400" />
                    Montant
                  </button>
                  <button 
                    onClick={() => setSearchQuery('payée')}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors text-left"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Payées
                  </button>
                  <button 
                    onClick={() => setSearchQuery('attente')}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors text-left"
                  >
                    <Clock className="h-4 w-4" />
                    En attente
                  </button>
                </div>
              </div>
            )}

            {/* Search results count */}
            {searchQuery && (
              <div className="flex items-center justify-between mt-3">
                <p className="text-sm text-slate-500">
                  <span className="font-medium text-slate-700">{filteredInvoices.length}</span> résultat(s) pour "{searchQuery}"
                </p>
                <button 
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Effacer la recherche
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Banner */}
      <Card className={`border ${complianceRate >= 100 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${complianceRate >= 100 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                <Shield className={`h-5 w-5 ${complianceRate >= 100 ? 'text-emerald-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className={`font-medium ${complianceRate >= 100 ? 'text-emerald-800' : 'text-amber-800'}`}>
                  Conformité e-facturation 2026
                </p>
                <p className={`text-sm ${complianceRate >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {dashboard?.compliance?.compliant_count || 0} factures conformes sur {dashboard?.total_invoices || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className={`text-2xl font-bold ${complianceRate >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {complianceRate}%
                </p>
                <p className="text-xs text-slate-500">Taux de conformité</p>
              </div>
              {complianceRate < 100 && (
                <Button variant="outline" size="sm" className="border-amber-300 text-amber-700">
                  Voir les alertes
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="border-slate-200" data-testid={stat.testId}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-xl bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Payées</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="paid-count">
                  {dashboard?.paid_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-xl bg-amber-50">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="pending-count">
                  {dashboard?.pending_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-xl bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En retard</p>
                <p className="text-2xl font-bold text-red-600" data-testid="overdue-count">
                  {dashboard?.overdue_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/scan')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-emerald-50">
                  <Camera className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Scanner un document</h3>
                  <p className="text-sm text-slate-500">Digitalisez factures et tickets avec l'IA</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/create')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-emerald-50">
                  <Sparkles className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Créer avec l'IA</h3>
                  <p className="text-sm text-slate-500">Génération automatique de factures</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-green-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/clients')} data-testid="clients-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-green-50">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Mes clients</h3>
                  <p className="text-sm text-slate-500">Gérez votre carnet de clients</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-orange-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/devis')} data-testid="quotes-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-orange-50">
                  <FileEdit className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Mes devis</h3>
                  <p className="text-sm text-slate-500">Créez et convertissez vos devis</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer col-span-2 md:col-span-1" onClick={() => navigate('/dashboard/smartfacture/inbox')} data-testid="inbox-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-100">
                  <Inbox className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Inbox SmartFacture</h3>
                  <p className="text-sm text-slate-500">Centralisez vos documents reçus</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer col-span-2 md:col-span-1" onClick={() => navigate('/dashboard/smartfacture/comptabilite')} data-testid="accounting-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-purple-100">
                  <Calculator className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Comptabilité & FEC</h3>
                  <p className="text-sm text-slate-500">Export pour votre comptable</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-teal-200 transition-colors cursor-pointer col-span-2 md:col-span-1" onClick={() => navigate('/dashboard/smartfacture/recurring')} data-testid="recurring-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-teal-100 to-emerald-100">
                  <RefreshCw className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Factures récurrentes</h3>
                  <p className="text-sm text-slate-500">Automatisez vos factures périodiques</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Features Row */}
        <Card className="border-slate-200 hover:border-red-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/relances')} data-testid="reminders-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-red-100 to-orange-100">
                  <Bell className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Relances automatiques</h3>
                  <p className="text-sm text-slate-500">Gérez les rappels impayés</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/dashboard-premium')} data-testid="dashboard-premium-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-100">
                  <BarChart3 className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Dashboard Premium</h3>
                  <p className="text-sm text-slate-500">Statistiques avancées & graphiques</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/export')} data-testid="export-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-green-100">
                  <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Export comptable</h3>
                  <p className="text-sm text-slate-500">CSV/Excel pour votre comptable</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/urssaf')} data-testid="urssaf-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-purple-100">
                  <Calculator className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Module URSSAF</h3>
                  <p className="text-sm text-slate-500">Simulateur cotisations sociales</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-cyan-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/banque')} data-testid="bank-reconciliation-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-100 to-emerald-100">
                  <CreditCard className="h-6 w-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Rapprochement bancaire</h3>
                  <p className="text-sm text-slate-500">Import CSV & matching factures</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-cyan-100 text-cyan-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-purple-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/analytics')} data-testid="simulator-analytics-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-100 to-emerald-100">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Analytics Simulateur</h3>
                  <p className="text-sm text-slate-500">Performance du quiz & leads</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Features - Templates, Import, Support, Team */}
        <Card className="border-slate-200 hover:border-amber-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/templates')} data-testid="templates-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100">
                  <Package className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Modèles de factures</h3>
                  <p className="text-sm text-slate-500">Créez et réutilisez vos modèles</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-100 text-amber-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-green-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/clients/import')} data-testid="import-clients-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100">
                  <Upload className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Importer clients CSV</h3>
                  <p className="text-sm text-slate-500">Migrez depuis Excel ou CSV</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700 text-xs">Nouveau</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/support')} data-testid="support-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-100">
                  <Headphones className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Support dédié</h3>
                  <p className="text-sm text-slate-500">Assistance prioritaire Expert</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Expert</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/team')} data-testid="team-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-purple-100">
                  <UsersRound className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Mon équipe</h3>
                  <p className="text-sm text-slate-500">Invitez vos collaborateurs</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Expert</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PPF - Portail Public de Facturation */}
        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/ppf')} data-testid="ppf-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-100">
                  <Building2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Portail Public (PPF)</h3>
                  <p className="text-sm text-slate-500">Connexion officielle à l'État</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Officiel</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Qonto - Plateforme Agréée */}
        <Card className="border-slate-200 hover:border-purple-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/qonto')} data-testid="qonto-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-100 to-emerald-100">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Qonto e-Invoicing</h3>
                  <p className="text-sm text-slate-500">Plateforme Agréée (PA)</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700 text-xs">Gratuit</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Application Mobile */}
        <Card className="border-slate-200 hover:border-emerald-200 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/smartfacture/mobile-app')} data-testid="mobile-app-link">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-cyan-100">
                  <Smartphone className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Application Mobile</h3>
                  <p className="text-sm text-slate-500">Installer sur votre téléphone</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">PWA</Badge>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {searchQuery ? 'Résultats de recherche' : 'Factures récentes'}
            </CardTitle>
            {!searchQuery && (
              <Button variant="ghost" size="sm" className="text-emerald-600">
                Voir tout
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInvoices?.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`invoice-${invoice.invoice_number}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <FileText className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                        {invoice.is_compliant ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-200 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Conforme
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            À vérifier
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{invoice.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {invoice.grand_total.toFixed(2)} €
                      </p>
                      <p className="text-xs text-slate-500">
                        Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Badge
                      className={
                        invoice.status === 'paid' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : invoice.status === 'pending' 
                            ? 'bg-amber-100 text-amber-700' 
                            : invoice.status === 'overdue'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-700'
                      }
                    >
                      {invoice.status === 'paid' ? 'Payée' : 
                       invoice.status === 'pending' ? 'En attente' :
                       invoice.status === 'overdue' ? 'En retard' :
                       invoice.status === 'draft' ? 'Brouillon' : invoice.status}
                    </Badge>
                    <div className="flex space-x-1">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0 hover:bg-emerald-50"
                        onClick={() => handleShare(invoice)}
                        title="Partager avec expert-comptable"
                        data-testid={`share-btn-${invoice.invoice_number}`}
                      >
                        <Share2 className="h-4 w-4 text-emerald-500" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => handleSimulate(invoice)}
                        title="Tester conformité PPF"
                        data-testid={`simulate-btn-${invoice.invoice_number}`}
                      >
                        <TestTube className="h-4 w-4 text-emerald-500" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => handleGeneratePDF(invoice.id)}
                      >
                        <Download className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4 text-slate-500" />
                      </Button>
                      {invoice.status !== 'paid' && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 w-8 p-0"
                        >
                          <Send className="h-4 w-4 text-slate-500" />
                        </Button>
                      )}
                      {invoice.status !== 'paid' ? (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-green-50"
                          onClick={() => handleMarkAsPaid(invoice)}
                          title="Marquer comme payée"
                          data-testid={`mark-paid-btn-${invoice.invoice_number}`}
                        >
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-amber-50"
                          onClick={() => handleMarkAsUnpaid(invoice)}
                          title="Remettre en attente"
                          data-testid={`mark-unpaid-btn-${invoice.invoice_number}`}
                        >
                          <Clock className="h-4 w-4 text-amber-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                {searchQuery ? (
                  <Search className="h-8 w-8 text-slate-400" />
                ) : (
                  <FileText className="h-8 w-8 text-slate-400" />
                )}
              </div>
              <p className="text-slate-600 font-medium">
                {searchQuery ? 'Aucun résultat trouvé' : 'Aucune facture'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {searchQuery 
                  ? `Aucune facture ne correspond à "${searchQuery}"`
                  : 'Créez votre première facture pour commencer'
                }
              </p>
              {searchQuery ? (
                <Button 
                  className="mt-4" 
                  variant="outline"
                  onClick={() => setSearchQuery('')}
                >
                  Effacer la recherche
                </Button>
              ) : (
                <Link to="/dashboard/smartfacture/create">
                  <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une facture
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PPF Simulation Modal */}
      <PPFSimulationModal
        isOpen={showSimulationModal}
        onClose={() => setShowSimulationModal(false)}
        invoice={selectedInvoice}
        onSimulationComplete={handleSimulationComplete}
        onTrialError={handleTrialError}
      />
      
      {/* Share Invoice Modal */}
      <ShareInvoiceModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        invoice={selectedInvoice}
        onShareComplete={() => loadDashboard()}
      />
      
      {/* Trial Paywall Modal */}
      <TrialPaywall 
        isOpen={showPaywall} 
        onClose={closePaywall}
        reason={paywallReason}
        actionType={paywallAction}
      />

      {/* QuickStart Wizard - Nouvel utilisateur */}
      {quickStartChecked && showQuickStart && (
        <QuickStartWizard 
          onComplete={() => {
            setShowQuickStart(false);
            loadDashboard(); // Refresh to show the new invoice
          }}
          onSkip={async () => {
            try {
              const token = localStorage.getItem('token');
              await fetch(`${API_URL}/api/quickstart/skip`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              });
            } catch (e) {
              console.error('Error skipping quickstart:', e);
            }
            setShowQuickStart(false);
          }}
        />
      )}

      {/* Onboarding Checklist - Only show if QuickStart is not shown */}
      {showOnboarding && !showQuickStart && (
        <OnboardingChecklist onClose={() => setShowOnboarding(false)} />
      )}

      {/* Testimonial Form - Show after user has created at least one invoice */}
      {dashboard?.recent_invoices?.length > 0 && (
        <div className="mt-6">
          <TestimonialForm userId={user?.id} />
        </div>
      )}
    </div>
  );
};

export default SmartFactureDashboard;
